/**
 * wikiForceGraph — self-contained force-directed graph engine for the wiki
 * side-panel (no 3rd-party dependency). Obsidian-style behavior:
 *
 *  - The simulation is INCREMENTAL: `tick(budgetMs)` advances it a few steps
 *    per animation frame under a time budget, so even a multi-thousand-node
 *    wiki never blocks the main thread — the layout visibly "unfolds" and
 *    settles, exactly like Obsidian's graph view.
 *  - Adding nodes (the growing trail) keeps existing positions and only
 *    re-heats the simulation, so the graph grows smoothly instead of
 *    re-laying-out from scratch.
 *  - Nodes are sized by link degree; hovering highlights the node and its
 *    neighbors while fading the rest; labels render under the nodes and fade
 *    in with zoom.
 *
 * Deterministic: initial positions come from a phyllotaxis spiral (no
 * Math.random), so the same wiki always lands in the same shape.
 */

import { WikiGlRenderer } from './wikiGlRenderer';

export interface GraphNodeInput { id: string; label: string; folder: string }
/** kind 'link' (default) = a real wikilink; 'jump' = the agent's temporal
 *  reading path, drawn dashed with a direction arrow. */
export interface GraphEdgeInput { source: string; target: string; kind?: 'link' | 'jump' }

interface SimNode {
  id: string;
  label: string;
  folder: string;
  x: number; y: number;
  vx: number; vy: number;
  /** Drag pin — while set, the node is frozen at this world position. */
  fx: number | null; fy: number | null;
  degree: number;
  r: number;
}

interface SimEdge {
  a: SimNode; b: SimNode;
  kind: 'link' | 'jump';
  /** d3-style stabilizers: without them a hub with dozens of links gets a net
   * spring gain > 1 per step and the layout oscillates divergently. */
  strength: number;   // 1 / min(degree a, degree b)
  bias: number;       // degree a / (degree a + degree b) — hubs move less
}

export interface GraphStyle {
  /** Ideal edge length in world units. */
  linkDistance: number;
  /** Node radius = baseRadius + sqrt(degree) * degreeRadius, clamped to maxRadius. */
  baseRadius: number;
  degreeRadius: number;
  maxRadius: number;
}

export interface RenderOptions {
  mode: 'trail' | 'full';
  currentId: string | null;
  hoverId: string | null;
  /** Node ids already visited by the agent (full mode); null → treat all as visited. */
  visited: Set<string> | null;
  /** Milliseconds timestamp driving the pulse animation. */
  time: number;
  /** Accent color for the trail / current-note highlight. */
  accent: string;
  folderColor: (folder: string) => string;
  /** Labels always visible (trail) or zoom-faded (full). */
  alwaysLabel: boolean;
  /** Obsidian-style uniform node fill (full map): every node this color with
   *  a soft same-hue aura; visited notes filled in the accent color instead.
   *  Unset → per-folder colors (trail). */
  nodeColor?: string;
  /** Focus color in uniform mode (Obsidian's purple): fills the current note,
   *  the hovered node, and tints their incident edges/aura. */
  highlight?: string;
  /** Animate the current-note pulse. When false (note hasn't changed in a
   *  while) the ring freezes, letting idle frames skip entirely. */
  pulse: boolean;
}

const TAU = Math.PI * 2;

export class WikiForceGraph {
  nodes: SimNode[] = [];
  private edges: SimEdge[] = [];
  private byId = new Map<string, SimNode>();
  private neighbors = new Map<string, Set<string>>();
  private style: GraphStyle = { linkDistance: 60, baseRadius: 4, degreeRadius: 1.5, maxRadius: 14 };

  /** Simulation heat — 0..1; the sim sleeps below alphaMin. */
  private alpha = 0;
  private readonly alphaMin = 0.004;
  private readonly alphaDecay = 0.028;

  // Camera: current values ease toward targets every frame.
  scale = 1; ox = 0; oy = 0;
  private tScale = 1; private tOx = 0; private tOy = 0;
  private snap = true; // first frame: jump straight to target

  // Render caches: while the simulation sleeps the geometry is static, so
  // edges/nodes/auras are drawn from prebuilt Path2D objects — one native
  // canvas call instead of a many-thousand-segment JS loop per frame. That
  // keeps zooming/panning a big settled graph smooth. Rebuilt after any tick.
  private pathsDirty = true;
  private edgePath: Path2D | null = null;
  private nodePath: Path2D | null = null;
  private auraPath: Path2D | null = null;
  private jumpList: SimEdge[] = [];

  // Offscreen scene + the state it was rendered with (see draw()). The canvas
  // only RECORDS the scene; what the frame loop blits is `sceneBitmap`, an
  // ImageBitmap materialized asynchronously — reading the canvas itself would
  // force Chromium's deferred rasterization flush inside the frame.
  private scene: HTMLCanvasElement | null = null;
  private sceneBitmap: ImageBitmap | null = null;
  private sceneBitmapCam = { scale: 1, ox: 0, oy: 0 };
  private sceneBitmapDpr = 1;
  private scenePending = false;
  private sceneCam = { scale: 1, ox: 0, oy: 0 };
  private sceneKey: {
    geom: number; hover: string | null; visited: Set<string> | null;
    color: string | undefined; w: number; h: number; dpr: number; alwaysLabel: boolean;
  } | null = null;
  private sceneTime = 0;
  private sceneCost = 0;
  /** Bumped whenever node positions/geometry change (any sim tick, new data). */
  private geomVersion = 0;
  /** What the visible canvas currently shows — lets idle frames skip. */
  private lastFrame: {
    bitmap: ImageBitmap | null; cam: string; hover: string | null;
    current: string | null; geom: number; w: number; h: number;
    visited: Set<string> | null;
  } | null = null;

  // WebGL path (full map): edges/nodes on the GPU, redrawn EVERY frame so
  // motion is Obsidian-smooth; the 2D canvas above it keeps labels + focus.
  private glr: WikiGlRenderer | null = null;
  private glCanvas: HTMLCanvasElement | null = null;
  private glGraphVersion = -1;
  private glGeomVersion = -1;
  private glVisitedRef: Set<string> | null | undefined;
  private graphVersion = 0;
  private indexById = new Map<string, number>();
  /** State the GL canvas currently shows — skips GL redraws (pulse animates
   *  only on the overlay, so the GL scene stays put between real changes). */
  private glScene: {
    cam: string; hover: string | null; geom: number;
    visited: Set<string> | null; w: number; h: number;
  } | null = null;
  // Software-rasterized WebGL (no GPU) is SLOWER than the bitmap pipeline.
  // GL calls only QUEUE work (the raster happens at flush, outside any timing
  // window), so the honest cost signal is the gap between consecutive
  // scene-rendering frames: ~16ms on a real GPU, 50-150ms in software. A
  // sustained high gap disables GL for the session and falls back to 2D.
  private glCostEma = 0;
  private glRenders = 0;
  private glDisabled = false;
  private glLastFrameTime = 0;

  /**
   * Replace the graph data. Nodes that already exist KEEP their position and
   * velocity; new nodes are seeded next to an already-placed neighbor (so the
   * trail grows outward) or on a phyllotaxis spiral.
   */
  setData(nodes: GraphNodeInput[], edges: GraphEdgeInput[], style: Partial<GraphStyle> = {}): void {
    this.style = { ...this.style, ...style };
    const prev = this.byId;
    this.byId = new Map();

    const adjacency = new Map<string, Set<string>>();
    for (const e of edges) {
      if (e.source === e.target) continue;
      let s = adjacency.get(e.source); if (!s) adjacency.set(e.source, (s = new Set()));
      let t = adjacency.get(e.target); if (!t) adjacency.set(e.target, (t = new Set()));
      s.add(e.target); t.add(e.source);
    }
    this.neighbors = adjacency;

    // A brand-new graph seeds ENTIRELY on the phyllotaxis spiral — spacing it
    // roughly at final density. Neighbor-seeding is only for incremental
    // additions (the growing trail): seeding a whole dense graph next to
    // already-placed neighbors piles everything up and the repulsion blast
    // destabilizes the first steps.
    const fresh = prev.size === 0;
    const placed: SimNode[] = [];
    let grew = false;
    nodes.forEach((n, i) => {
      const old = prev.get(n.id);
      if (old) {
        old.label = n.label; old.folder = n.folder;
        this.byId.set(n.id, old);
        placed.push(old);
        return;
      }
      grew = true;
      const angle = i * 2.399963229728653; // golden angle
      const near = fresh
        ? undefined
        : [...(adjacency.get(n.id) ?? [])]
          .map((id) => this.byId.get(id) ?? prev.get(id))
          .find(Boolean);
      let x: number; let y: number;
      if (near) {
        x = near.x + Math.cos(angle) * this.style.linkDistance * 0.6;
        y = near.y + Math.sin(angle) * this.style.linkDistance * 0.6;
      } else {
        const rad = Math.sqrt(i + 1) * this.style.linkDistance * 0.7;
        x = Math.cos(angle) * rad;
        y = Math.sin(angle) * rad;
      }
      const node: SimNode = { id: n.id, label: n.label, folder: n.folder, x, y, vx: 0, vy: 0, fx: null, fy: null, degree: 0, r: 0 };
      this.byId.set(n.id, node);
      placed.push(node);
    });
    this.nodes = placed;

    for (const n of this.nodes) {
      n.degree = adjacency.get(n.id)?.size ?? 0;
      n.r = Math.min(this.style.maxRadius, this.style.baseRadius + Math.sqrt(n.degree) * this.style.degreeRadius);
    }

    this.edges = [];
    const seen = new Set<string>();
    for (const e of edges) {
      const a = this.byId.get(e.source); const b = this.byId.get(e.target);
      if (!a || !b || a === b) continue;
      const key = a.id < b.id ? `${a.id}|${b.id}` : `${b.id}|${a.id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      this.edges.push({
        a, b,
        kind: e.kind ?? 'link',
        strength: 1 / Math.max(1, Math.min(a.degree, b.degree)),
        bias: a.degree / Math.max(1, a.degree + b.degree),
      });
    }

    this.jumpList = this.edges.filter((e) => e.kind === 'jump');
    this.indexById.clear();
    this.nodes.forEach((n, i) => this.indexById.set(n.id, i));
    this.pathsDirty = true;
    this.geomVersion++;
    this.graphVersion++;

    const removed = prev.size !== this.nodes.length && prev.size > 0;
    if (grew || removed) this.reheat(prev.size === 0 ? 1 : 0.45);
  }

  reheat(alpha = 0.4): void { this.alpha = Math.max(this.alpha, alpha); }
  get settled(): boolean { return this.alpha < this.alphaMin; }
  get nodeCount(): number { return this.nodes.length; }

  neighborsOf(id: string): Set<string> | undefined { return this.neighbors.get(id); }
  getNode(id: string): SimNode | undefined { return this.byId.get(id); }

  /**
   * Advance the simulation for at most `budgetMs` of wall time. Runs at least
   * one step while hot, so huge graphs simply take a few more frames to settle
   * instead of freezing the UI.
   */
  tick(budgetMs = 6): void {
    if (this.alpha < this.alphaMin) return;
    const start = performance.now();
    do {
      this.step();
      this.alpha *= 1 - this.alphaDecay;
    } while (this.alpha >= this.alphaMin && performance.now() - start < budgetMs);
    this.pathsDirty = true;
    this.geomVersion++;
  }

  private step(): void {
    const { linkDistance: L } = this.style;
    const alpha = this.alpha;
    const nodes = this.nodes;
    // Hard ceilings keep the integration unconditionally stable, whatever the
    // wiki's density — a hub with dozens of links must never gain more
    // velocity per step than damping can absorb.
    const maxForce = L * 0.9;   // net per-force-pass velocity gain per node
    const maxSpeed = L * 0.9;   // total velocity per step
    const worldCap = L * 400;   // sanity box for positions

    // Many-body repulsion: exact pairwise within the 3×3 cell neighborhood,
    // plus a single-level Barnes-Hut far field (each distant CELL acts as one
    // body at its centroid). Without the far field, nodes outside the exact
    // window feel zero push and the layout condenses into lattice-aligned
    // density bands.
    const REP = L * 4;
    const cell = L * 1.6;
    const grid = new Map<number, SimNode[]>();
    const gkey = (cx: number, cy: number) => cx * 73856093 ^ cy * 19349663;
    for (const n of nodes) {
      const key = gkey(Math.floor(n.x / cell), Math.floor(n.y / cell));
      const bucket = grid.get(key);
      if (bucket) bucket.push(n); else grid.set(key, [n]);
    }
    const clusters: { x: number; y: number; count: number; cx: number; cy: number }[] = [];
    grid.forEach((bucket) => {
      let sx = 0; let sy = 0;
      for (const o of bucket) { sx += o.x; sy += o.y; }
      const x = sx / bucket.length; const y = sy / bucket.length;
      clusters.push({ x, y, count: bucket.length, cx: Math.floor(x / cell), cy: Math.floor(y / cell) });
    });
    for (const n of nodes) {
      const cx = Math.floor(n.x / cell); const cy = Math.floor(n.y / cell);
      let fx = 0; let fy = 0;
      for (let dx = -1; dx <= 1; dx++) {
        for (let dy = -1; dy <= 1; dy++) {
          const bucket = grid.get(gkey(cx + dx, cy + dy));
          if (!bucket) continue;
          for (const o of bucket) {
            if (o === n) continue;
            const ddx = n.x - o.x; const ddy = n.y - o.y;
            const d2 = Math.max(ddx * ddx + ddy * ddy, 1);
            const f = (REP * alpha) / d2;
            fx += ddx * f; fy += ddy * f;
          }
        }
      }
      for (const c of clusters) {
        if (Math.abs(c.cx - cx) <= 1 && Math.abs(c.cy - cy) <= 1) continue; // covered exactly
        const ddx = n.x - c.x; const ddy = n.y - c.y;
        const d2 = Math.max(ddx * ddx + ddy * ddy, 1);
        const f = (c.count * REP * alpha) / d2;
        fx += ddx * f; fy += ddy * f;
      }
      const mag = Math.sqrt(fx * fx + fy * fy);
      if (mag > maxForce) { fx *= maxForce / mag; fy *= maxForce / mag; }
      n.vx += fx; n.vy += fy;
    }

    // Link springs, d3-style: strength normalized by the smaller endpoint
    // degree and split by bias so hubs stay put while leaves swing.
    for (const { a, b, strength, bias } of this.edges) {
      const dx = (b.x + b.vx) - (a.x + a.vx);
      const dy = (b.y + b.vy) - (a.y + a.vy);
      const d = Math.max(Math.sqrt(dx * dx + dy * dy), 1);
      const f = ((d - L) / d) * alpha * strength;
      b.vx -= dx * f * bias; b.vy -= dy * f * bias;
      a.vx += dx * f * (1 - bias); a.vy += dy * f * (1 - bias);
    }

    // Weak gravity toward the origin keeps disconnected clusters on screen.
    for (const n of nodes) {
      n.vx -= n.x * alpha * 0.008;
      n.vy -= n.y * alpha * 0.008;
      n.vx *= 0.6; n.vy *= 0.6;
      const speed = Math.sqrt(n.vx * n.vx + n.vy * n.vy);
      if (speed > maxSpeed) { n.vx *= maxSpeed / speed; n.vy *= maxSpeed / speed; }
      if (n.fx !== null && n.fy !== null) {
        n.x = n.fx; n.y = n.fy; n.vx = 0; n.vy = 0;
        continue;
      }
      n.x += n.vx; n.y += n.vy;
      if (!Number.isFinite(n.x) || !Number.isFinite(n.y)) { n.x = 0; n.y = 0; n.vx = 0; n.vy = 0; }
      else {
        if (n.x > worldCap) n.x = worldCap; else if (n.x < -worldCap) n.x = -worldCap;
        if (n.y > worldCap) n.y = worldCap; else if (n.y < -worldCap) n.y = -worldCap;
      }
    }
  }

  // --- Camera ---------------------------------------------------------------

  private bounds(): { minX: number; minY: number; maxX: number; maxY: number } {
    let minX = Infinity; let minY = Infinity; let maxX = -Infinity; let maxY = -Infinity;
    for (const n of this.nodes) {
      if (n.x < minX) minX = n.x; if (n.x > maxX) maxX = n.x;
      if (n.y < minY) minY = n.y; if (n.y > maxY) maxY = n.y;
    }
    if (minX === Infinity) return { minX: -1, minY: -1, maxX: 1, maxY: 1 };
    return { minX, minY, maxX, maxY };
  }

  /** Scale that frames the whole graph inside view (used by follow-zoom too). */
  fitScale(viewW: number, viewH: number, maxScale = 1.5): number {
    const b = this.bounds();
    const pad = 56;
    const w = Math.max(1, b.maxX - b.minX + pad * 2);
    const h = Math.max(1, b.maxY - b.minY + pad * 2);
    return Math.min(maxScale, Math.min(viewW / w, viewH / h) * 0.92) || 1;
  }

  /** Ease the camera to frame the whole graph. */
  fitAll(viewW: number, viewH: number, maxScale = 1.5): void {
    const b = this.bounds();
    const s = this.fitScale(viewW, viewH, maxScale);
    this.tScale = s;
    this.tOx = viewW / 2 - ((b.minX + b.maxX) / 2) * s;
    this.tOy = viewH / 2 - ((b.minY + b.maxY) / 2) * s;
  }

  /** Ease the camera to center a node at the given zoom. */
  centerOn(id: string, viewW: number, viewH: number, scale: number): void {
    const n = this.byId.get(id);
    if (!n) return;
    this.tScale = scale;
    this.tOx = viewW / 2 - n.x * scale;
    this.tOy = viewH / 2 - n.y * scale;
  }

  zoomAt(sx: number, sy: number, factor: number): void {
    const next = Math.max(0.08, Math.min(6, this.tScale * factor));
    // Keep the world point under the cursor fixed while zooming.
    const wx = (sx - this.tOx) / this.tScale;
    const wy = (sy - this.tOy) / this.tScale;
    this.tScale = next;
    this.tOx = sx - wx * next;
    this.tOy = sy - wy * next;
  }

  panBy(dx: number, dy: number): void {
    this.tOx += dx; this.tOy += dy;
    // Pan tracks the pointer 1:1 — no easing lag.
    this.ox = this.tOx; this.oy = this.tOy;
  }

  private easeCamera(): void {
    if (this.snap) {
      this.scale = this.tScale; this.ox = this.tOx; this.oy = this.tOy;
      this.snap = false;
      return;
    }
    this.scale += (this.tScale - this.scale) * 0.14;
    this.ox += (this.tOx - this.ox) * 0.14;
    this.oy += (this.tOy - this.oy) * 0.14;
    // Land exactly on the target once close — an asymptotic tail would keep
    // "camera moved" true (and scene repaints firing) long after motion is
    // visually over.
    if (Math.abs(this.tScale - this.scale) < 1e-3
      && Math.abs(this.tOx - this.ox) < 0.3 && Math.abs(this.tOy - this.oy) < 0.3) {
      this.scale = this.tScale; this.ox = this.tOx; this.oy = this.tOy;
    }
  }

  resetCamera(): void { this.snap = true; }

  screenToWorld(sx: number, sy: number): { x: number; y: number } {
    return { x: (sx - this.ox) / this.scale, y: (sy - this.oy) / this.scale };
  }

  /** Topmost node whose circle (plus slack) contains the screen point. */
  hitTest(sx: number, sy: number): SimNode | null {
    const { x, y } = this.screenToWorld(sx, sy);
    const slack = 5 / this.scale;
    let best: SimNode | null = null;
    let bestD = Infinity;
    for (const n of this.nodes) {
      const dx = n.x - x; const dy = n.y - y;
      const d = Math.sqrt(dx * dx + dy * dy) - n.r;
      if (d < slack && d < bestD) { bestD = d; best = n; }
    }
    return best;
  }

  // --- Rendering --------------------------------------------------------------
  //
  // Two layers, so a big graph never dictates the frame rate:
  //  1. The SCENE (edges, auras, nodes, labels) renders into an offscreen
  //     canvas, throttled adaptively — a scene that takes long to paint is
  //     repainted less often. Small graphs repaint every frame.
  //  2. Every animation frame only blits that bitmap through the current
  //     camera transform and draws the live bits (pulse ring, current label)
  //     on top — a handful of native calls, always 60fps pan/zoom.

  draw(cv: HTMLCanvasElement, opts: RenderOptions, glCanvas?: HTMLCanvasElement | null): void {
    const dpr = window.devicePixelRatio || 1;
    const vw = cv.clientWidth; const vh = cv.clientHeight;
    if (vw === 0 || vh === 0) return;
    if (cv.width !== vw * dpr || cv.height !== vh * dpr) { cv.width = vw * dpr; cv.height = vh * dpr; }
    const ctx = cv.getContext('2d');
    if (!ctx) return;

    this.easeCamera();
    const s = this.scale || 1;

    // Small graphs (the trail) are cheap enough to paint from scratch every
    // frame — that is the smoothest possible path.
    if (this.nodes.length < 150) {
      this.drawDirect(ctx, opts, vw, vh, dpr);
      return;
    }

    // Big graphs: GPU path when available — edges/nodes redraw live on any
    // change (Obsidian-smooth motion, crisp at every zoom), labels on this
    // canvas. Auto-disabled when WebGL turns out to be software-rasterized.
    if (glCanvas && !this.glDisabled) {
      if (this.glCanvas !== glCanvas || !this.glr) {
        this.glr = new WikiGlRenderer(glCanvas);
        this.glCanvas = glCanvas;
        this.glGraphVersion = -1;
        this.glScene = null;
      }
      if (this.glr.ok) {
        this.drawGl(ctx, opts, vw, vh, dpr, s);
        return;
      }
    }

    // Re-render the scene when its content changed or the camera moved, spaced
    // by at least 3× the last render's own cost — an expensive scene degrades
    // to fewer scene updates (the blit keeps interaction at full frame rate),
    // never to a frozen UI. When nothing changes, no repaints happen at all.
    // Zoomed out, fine detail is invisible, so the bitmap drops to dpr 1 —
    // a large fill-rate saving exactly where the scene is heaviest; very
    // dense graphs cap at 1.5 even zoomed in.
    const sdpr = s < 0.7 ? Math.min(1, dpr) : this.edges.length > 5000 ? Math.min(1.5, dpr) : dpr;
    const k = this.sceneKey;
    const contentChanged = !k
      || k.geom !== this.geomVersion || k.hover !== opts.hoverId
      || k.visited !== opts.visited || k.color !== opts.nodeColor
      || k.w !== vw || k.h !== vh || k.dpr !== sdpr || k.alwaysLabel !== opts.alwaysLabel;
    const cameraMoved = Math.abs(s - this.sceneCam.scale) > 1e-4
      || Math.abs(this.ox - this.sceneCam.ox) > 0.25 || Math.abs(this.oy - this.sceneCam.oy) > 0.25;
    const throttle = Math.min(4000, Math.max(16, this.sceneCost * 3));
    if (!k || (!this.scenePending && (contentChanged || cameraMoved) && opts.time - this.sceneTime >= throttle)) {
      this.renderScene(vw, vh, sdpr, opts);
    }

    // TRUE idle: the canvas already shows exactly this frame — do nothing.
    // (With the pulse window expired, a settled graph costs zero per frame.)
    if (this.frameUnchanged(opts, this.sceneBitmap, s, vw, vh)) return;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, vw, vh);
    const bmp = this.sceneBitmap;
    if (bmp) {
      // Blit the materialized scene through the delta between the camera it
      // was rendered at and the camera now (momentarily soft while zooming;
      // the next scene render re-sharpens). This is O(destination pixels).
      const zk = s / (this.sceneBitmapCam.scale || 1);
      ctx.drawImage(
        bmp,
        0, 0, bmp.width, bmp.height,
        this.ox - this.sceneBitmapCam.ox * zk, this.oy - this.sceneBitmapCam.oy * zk,
        (bmp.width / this.sceneBitmapDpr) * zk, (bmp.height / this.sceneBitmapDpr) * zk,
      );
    } else if (this.scene && this.sceneKey) {
      // Only before the very first bitmap resolves: blit the canvas directly
      // (this pays one synchronous rasterization flush, once).
      const zk = s / (this.sceneCam.scale || 1);
      ctx.drawImage(
        this.scene,
        0, 0, this.scene.width, this.scene.height,
        this.ox - this.sceneCam.ox * zk, this.oy - this.sceneCam.oy * zk,
        (this.scene.width / this.sceneKey.dpr) * zk, (this.scene.height / this.sceneKey.dpr) * zk,
      );
    }

    this.drawLive(ctx, opts);
  }

  /** True (and remembers the frame) when the canvas already shows exactly
   *  this state — the idle skip shared by all three render paths. */
  private frameUnchanged(opts: RenderOptions, bitmap: ImageBitmap | null, s: number, vw: number, vh: number): boolean {
    const camKey = `${s}|${this.ox}|${this.oy}`;
    const lf = this.lastFrame;
    if (!opts.pulse && lf
      && lf.bitmap === bitmap && lf.cam === camKey
      && lf.hover === opts.hoverId && lf.current === opts.currentId
      && lf.geom === this.geomVersion && lf.w === vw && lf.h === vh
      && lf.visited === opts.visited) {
      return true;
    }
    this.lastFrame = {
      bitmap, cam: camKey, hover: opts.hoverId, current: opts.currentId,
      geom: this.geomVersion, w: vw, h: vh, visited: opts.visited,
    };
    return false;
  }

  /** Small graphs: paint the whole scene directly, every frame. */
  private drawDirect(ctx: CanvasRenderingContext2D, opts: RenderOptions, vw: number, vh: number, dpr: number): void {
    if (this.frameUnchanged(opts, null, this.scale, vw, vh)) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, vw, vh);
    this.paintScene(ctx, vw, vh, opts);
    this.drawLive(ctx, opts);
  }

  /** GPU path: WebGL draws edges/nodes below; this canvas gets labels + focus. */
  private drawGl(ctx: CanvasRenderingContext2D, opts: RenderOptions, vw: number, vh: number, dpr: number, s: number): void {
    const glr = this.glr!;
    // Sync graph structure / positions / visited flags into GL buffers.
    if (this.glGraphVersion !== this.graphVersion || this.glVisitedRef !== opts.visited) {
      const pairs: [number, number][] = [];
      for (const e of this.edges) {
        if (e.kind === 'jump') continue;
        const a = this.indexById.get(e.a.id); const b = this.indexById.get(e.b.id);
        if (a !== undefined && b !== undefined) pairs.push([a, b]);
      }
      const visitedIdx = new Set<number>();
      opts.visited?.forEach((id) => {
        const i = this.indexById.get(id);
        if (i !== undefined) visitedIdx.add(i);
      });
      glr.setGraph(this.nodes, pairs, visitedIdx);
      this.glGraphVersion = this.graphVersion;
      this.glGeomVersion = this.geomVersion;
      this.glVisitedRef = opts.visited;
    } else if (this.glGeomVersion !== this.geomVersion) {
      glr.updatePositions(this.nodes);
      this.glGeomVersion = this.geomVersion;
    }

    // Redraw the GL scene only when it actually changed — the pulse animation
    // lives on the 2D overlay, so a settled graph leaves the GL layer alone
    // (preserveDrawingBuffer keeps it composited).
    const camKey = `${s}|${this.ox}|${this.oy}`;
    const gs = this.glScene;
    const sceneChanged = !gs || gs.cam !== camKey || gs.hover !== opts.hoverId
      || gs.geom !== this.geomVersion || gs.visited !== opts.visited
      || gs.w !== vw || gs.h !== vh;
    if (sceneChanged) {
      const hoverIndex = opts.hoverId ? this.indexById.get(opts.hoverId) ?? -1 : -1;
      let litIndices: number[] | null = null;
      if (hoverIndex >= 0 && opts.hoverId) {
        litIndices = [hoverIndex];
        this.neighbors.get(opts.hoverId)?.forEach((id) => {
          const i = this.indexById.get(id);
          if (i !== undefined) litIndices!.push(i);
        });
      }
      glr.render({ scale: s, ox: this.ox, oy: this.oy }, {
        nodeColor: opts.nodeColor ?? '#7d8590',
        highlight: opts.highlight ?? opts.accent,
        accent: opts.accent,
        hoverIndex,
        litIndices,
        aura: s >= 0.5,
      });
      // Gap between consecutive scene-rendering frames = true per-frame cost
      // during continuous motion. Gaps >300ms mean the tab was throttled or
      // motion paused — not render cost — and are ignored.
      const gap = this.glLastFrameTime ? opts.time - this.glLastFrameTime : 0;
      this.glLastFrameTime = opts.time;
      if (gap > 0 && gap < 300) {
        this.glRenders++;
        this.glCostEma = this.glCostEma ? this.glCostEma * 0.8 + gap * 0.2 : gap;
        if (this.glRenders >= 15 && this.glCostEma > 55) {
          // No real GPU behind this context — the bitmap pipeline is faster.
          this.glDisabled = true;
          glr.clear();
          this.lastFrame = null;
          return;
        }
      }
      this.glScene = {
        cam: camKey, hover: opts.hoverId, geom: this.geomVersion,
        visited: opts.visited, w: vw, h: vh,
      };
    }

    if (!sceneChanged && this.frameUnchanged(opts, null, s, vw, vh)) return;
    if (sceneChanged) this.frameUnchanged(opts, null, s, vw, vh); // record

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, vw, vh);
    const hover = opts.hoverId ? this.byId.get(opts.hoverId) ?? null : null;
    const hoverSet = hover ? this.neighbors.get(hover.id) ?? new Set<string>() : null;
    const isLit = (id: string) => !hover || id === hover.id || !!hoverSet?.has(id);
    this.drawLabels(ctx, opts, hover, isLit, vw, vh);
    this.drawLive(ctx, opts);
  }

  /** The current note — purple focus disc/aura in the full map, pulse ring
   *  while the agent is moving, and its always-on label. */
  private drawLive(ctx: CanvasRenderingContext2D, opts: RenderOptions): void {
    if (!opts.currentId) return;
    const n = this.byId.get(opts.currentId);
    if (!n) return;
    const s = this.scale || 1;
    const hl = opts.nodeColor ? (opts.highlight ?? opts.accent) : opts.accent;
    const cx = this.ox + n.x * s; const cy = this.oy + n.y * s;
    const r = n.r * s;
    if (opts.nodeColor) {
      ctx.fillStyle = hl;
      ctx.globalAlpha = 0.22;
      ctx.beginPath();
      ctx.arc(cx, cy, r * 2.4 + 4, 0, TAU);
      ctx.fill();
      ctx.globalAlpha = 1;
      ctx.beginPath();
      ctx.arc(cx, cy, r + 1, 0, TAU);
      ctx.fill();
    }
    const pulse = opts.pulse ? 4 + Math.sin(opts.time / 220) * 2.5 : 4;
    ctx.beginPath();
    ctx.strokeStyle = hl;
    ctx.globalAlpha = 0.9;
    ctx.lineWidth = 2;
    ctx.arc(cx, cy, r + 3 + pulse, 0, TAU);
    ctx.stroke();
    ctx.globalAlpha = 1;
    const label = n.label.length > 28 ? `${n.label.slice(0, 27)}…` : n.label;
    ctx.font = '600 11px -apple-system, "Segoe UI", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.88)';
    ctx.lineWidth = 3;
    ctx.strokeText(label, cx, cy + r + 12);
    ctx.fillStyle = hl;
    ctx.fillText(label, cx, cy + r + 12);
  }

  private renderScene(vw: number, vh: number, dpr: number, opts: RenderOptions): void {
    const t0 = performance.now();
    if (!this.scene) this.scene = document.createElement('canvas');
    const cv = this.scene;
    if (cv.width !== vw * dpr || cv.height !== vh * dpr) { cv.width = vw * dpr; cv.height = vh * dpr; }
    const ctx = cv.getContext('2d');
    if (!ctx) return;
    const s = this.scale || 1;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, vw, vh);
    this.paintScene(ctx, vw, vh, opts);

    this.sceneCam = { scale: s, ox: this.ox, oy: this.oy };
    this.sceneKey = {
      geom: this.geomVersion,
      hover: opts.hoverId,
      visited: opts.visited,
      color: opts.nodeColor,
      w: vw, h: vh, dpr,
      alwaysLabel: opts.alwaysLabel,
    };

    // Materialize asynchronously: createImageBitmap snapshots the canvas and
    // performs the (possibly expensive) rasterization outside the frame loop.
    // Until it resolves, frames keep blitting the previous bitmap. The cost
    // and completion time stamp when the bitmap actually landed, so the
    // throttle spaces repaints by their true end-to-end price.
    const cam = { scale: s, ox: this.ox, oy: this.oy };
    this.scenePending = true;
    createImageBitmap(cv).then((bmp) => {
      this.sceneBitmap?.close();
      this.sceneBitmap = bmp;
      this.sceneBitmapCam = cam;
      this.sceneBitmapDpr = dpr;
    }).catch(() => { /* keep blitting the previous bitmap */ })
      .then(() => {
        this.scenePending = false;
        this.sceneTime = performance.now();
        this.sceneCost = this.sceneTime - t0;
      });
  }

  /** Full 2D scene paint (edges, nodes, labels) at the current camera. */
  private paintScene(ctx: CanvasRenderingContext2D, vw: number, vh: number, opts: RenderOptions): void {
    const s = this.scale || 1;
    ctx.save();
    ctx.translate(this.ox, this.oy);
    ctx.scale(s, s);

    const hover = opts.hoverId ? this.byId.get(opts.hoverId) ?? null : null;
    const hoverSet = hover ? this.neighbors.get(hover.id) ?? new Set<string>() : null;
    const isLit = (id: string) => !hover || id === hover.id || !!hoverSet?.has(id);

    // Viewport in world coordinates (with margin) for culling.
    const m = 60 / s;
    const wx0 = -this.ox / s - m; const wy0 = -this.oy / s - m;
    const wx1 = (vw - this.ox) / s + m; const wy1 = (vh - this.oy) / s + m;
    const inView = (n: SimNode) => n.x >= wx0 && n.x <= wx1 && n.y >= wy0 && n.y <= wy1;

    // Rebuild the static render paths only when the simulation moved.
    if (this.pathsDirty || !this.edgePath) {
      const ep = new Path2D(); const np = new Path2D(); const ap = new Path2D();
      for (const e of this.edges) {
        if (e.kind === 'jump') continue;
        ep.moveTo(e.a.x, e.a.y); ep.lineTo(e.b.x, e.b.y);
      }
      for (const n of this.nodes) {
        np.moveTo(n.x + n.r, n.y); np.arc(n.x, n.y, n.r, 0, TAU);
        const ar = n.r * 2.1;
        ap.moveTo(n.x + ar, n.y); ap.arc(n.x, n.y, ar, 0, TAU);
      }
      this.edgePath = ep; this.nodePath = np; this.auraPath = ap;
      this.pathsDirty = false;
    }

    // --- Edges. Real wikilinks are solid gray; the agent's temporal jump path
    // ('jump' edges) is dashed in the accent color with direction arrows.
    // Width is screen-constant (~1px) at every zoom, like Obsidian. Hovering
    // lights the hovered node's links and fades everything else.
    const lw = 1.1 / s;
    const jumps = this.jumpList;
    const highlight = opts.highlight ?? opts.accent;
    // Obsidian keeps the full map's mesh whisper-faint; the trail's few links
    // read stronger.
    const baseEdgeAlpha = opts.nodeColor ? 0.15 : 0.28;
    ctx.lineWidth = lw;
    ctx.strokeStyle = `rgba(122, 132, 148, ${hover ? 0.06 : baseEdgeAlpha})`;
    ctx.stroke(this.edgePath);
    if (hover) {
      // Lit pass: re-stroke just the hovered node's links on top — purple in
      // the full map (like Obsidian's focus), slate in the trail.
      ctx.strokeStyle = opts.nodeColor ? highlight : 'rgba(71, 85, 105, 0.85)';
      ctx.globalAlpha = 0.5;
      ctx.lineWidth = lw * 1.6;
      ctx.beginPath();
      for (const e of this.edges) {
        if (e.kind === 'jump' || (e.a !== hover && e.b !== hover)) continue;
        ctx.moveTo(e.a.x, e.a.y); ctx.lineTo(e.b.x, e.b.y);
      }
      ctx.stroke();
      ctx.globalAlpha = 1;
      ctx.lineWidth = lw;
    }
    if (jumps.length) {
      ctx.save();
      ctx.setLineDash([5 / s, 4 / s]);
      ctx.lineWidth = lw * 1.3;
      for (const e of jumps) {
        const incident = !!hover && (e.a === hover || e.b === hover);
        ctx.globalAlpha = hover && !incident ? 0.15 : 1;
        ctx.strokeStyle = incident ? 'rgba(37, 99, 235, 0.75)' : 'rgba(37, 99, 235, 0.4)';
        ctx.beginPath();
        ctx.moveTo(e.a.x, e.a.y); ctx.lineTo(e.b.x, e.b.y);
        ctx.stroke();
      }
      ctx.restore();
      ctx.globalAlpha = 1;
      // Direction arrows at the jump midpoints (reading order).
      ctx.fillStyle = 'rgba(37, 99, 235, 0.55)';
      const ah = 5 / Math.sqrt(s);
      for (const e of jumps) {
        const dx = e.b.x - e.a.x; const dy = e.b.y - e.a.y;
        const d = Math.sqrt(dx * dx + dy * dy);
        if (d < ah * 4) continue;
        const mx = (e.a.x + e.b.x) / 2; const my = (e.a.y + e.b.y) / 2;
        const ux = dx / d; const uy = dy / d;
        ctx.beginPath();
        ctx.moveTo(mx + ux * ah, my + uy * ah);
        ctx.lineTo(mx - ux * ah * 0.7 - uy * ah * 0.62, my - uy * ah * 0.7 + ux * ah * 0.62);
        ctx.lineTo(mx - ux * ah * 0.7 + uy * ah * 0.62, my - uy * ah * 0.7 - ux * ah * 0.62);
        ctx.closePath();
        ctx.fill();
      }
    }
    // --- Nodes.
    if (opts.nodeColor) {
      // Obsidian-style full map: every node the same gray with a soft aura,
      // drawn from the cached paths; visited notes re-filled in the accent.
      // The aura is invisible mush below ~half zoom — skip its fill cost there.
      ctx.fillStyle = opts.nodeColor;
      if (s >= 0.5) {
        ctx.globalAlpha = hover ? 0.04 : 0.08;
        ctx.fill(this.auraPath!);
      }
      ctx.globalAlpha = hover ? 0.22 : 1;
      ctx.fill(this.nodePath!);
      ctx.globalAlpha = 1;
      if (hover) {
        // Neighbors back at full strength; the hovered node itself goes
        // purple with a soft purple aura (the Obsidian focus look).
        for (const n of this.nodes) {
          if (!isLit(n.id) || !inView(n) || n === hover) continue;
          ctx.beginPath();
          ctx.arc(n.x, n.y, n.r, 0, TAU);
          ctx.fill();
        }
        ctx.fillStyle = highlight;
        ctx.globalAlpha = 0.25;
        ctx.beginPath();
        ctx.arc(hover.x, hover.y, hover.r * 2.4, 0, TAU);
        ctx.fill();
        ctx.globalAlpha = 1;
        ctx.beginPath();
        ctx.arc(hover.x, hover.y, hover.r * 1.25, 0, TAU);
        ctx.fill();
      }
      if (opts.visited && opts.visited.size) {
        ctx.fillStyle = opts.accent;
        for (const n of this.nodes) {
          if (!opts.visited.has(n.id) || !inView(n)) continue;
          ctx.globalAlpha = isLit(n.id) ? 1 : 0.14;
          ctx.beginPath();
          ctx.arc(n.x, n.y, (hover === n ? n.r * 1.25 : n.r) + 0.6, 0, TAU);
          ctx.fill();
        }
        ctx.globalAlpha = 1;
      }
    } else {
      // Trail: few nodes, folder-colored, white-rimmed.
      for (const n of this.nodes) {
        if (!inView(n)) continue;
        ctx.globalAlpha = isLit(n.id) ? 1 : 0.14;
        ctx.beginPath();
        ctx.fillStyle = opts.folderColor(n.folder);
        ctx.arc(n.x, n.y, hover === n ? n.r * 1.25 : n.r, 0, TAU);
        ctx.fill();
        ctx.lineWidth = 1.5 / s;
        ctx.strokeStyle = '#fff';
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
    }

    ctx.restore();

    this.drawLabels(ctx, opts, hover, isLit, vw, vh);
  }

  /** Screen-space labels, zoom-faded; the hovered note is always labeled
   *  (the current note's label lives in drawLive). */
  private drawLabels(
    ctx: CanvasRenderingContext2D, opts: RenderOptions,
    hover: SimNode | null, isLit: (id: string) => boolean,
    vw: number, vh: number,
  ): void {
    const s = this.scale || 1;
    // Fade-in starts where neighboring labels stop colliding (comfortable
    // around s≈1.2 for ~46-unit node spacing).
    const zoomAlpha = opts.alwaysLabel ? 1 : Math.max(0, Math.min(1, (s - 0.9) / 0.6));
    if (zoomAlpha <= 0.02 && !hover) return;
    ctx.font = `${opts.alwaysLabel ? 600 : 400} 11px -apple-system, "Segoe UI", sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.lineJoin = 'round';
    for (const n of this.nodes) {
      if (n.id === opts.currentId) continue;
      const sx = this.ox + n.x * s;
      const sy = this.oy + (n.y + n.r) * s + 4;
      if (sx < -90 || sx > vw + 90 || sy < -30 || sy > vh + 30) continue;
      const isHover = hover === n;
      const a = isHover ? 1 : zoomAlpha * (isLit(n.id) ? 1 : 0.12);
      if (a < 0.02) continue;
      const label = n.label.length > 28 ? `${n.label.slice(0, 27)}…` : n.label;
      ctx.globalAlpha = a;
      if (opts.alwaysLabel || isHover) {
        // White halo for legibility — only on the few trail/hover labels;
        // strokeText over hundreds of full-map labels would cost real frames.
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.88)';
        ctx.lineWidth = 3;
        ctx.strokeText(label, sx, sy);
      }
      ctx.fillStyle = isHover ? '#1e293b' : '#475569';
      ctx.fillText(label, sx, sy);
    }
    ctx.globalAlpha = 1;
  }
}
